const TEXT_FILE_MAX_BYTES = 2 * 1024 * 1024;
const DOCUMENT_FILE_MAX_BYTES = 15 * 1024 * 1024;
export const MAX_IMPORTED_SCRIPT_CHARACTERS = 200000;

function extensionOf(name = '') {
  const match = String(name).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || '';
}

function cleanExtractedText(value) {
  return String(value || '')
    .replace(/\r/g, '')
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}

function validateFile(file) {
  if (!file || typeof file.arrayBuffer !== 'function') throw new Error('Choose a script file first.');
  const extension = extensionOf(file.name);
  if (!['pdf', 'docx', 'txt', 'md'].includes(extension)) throw new Error('Choose a PDF, DOCX, TXT, or Markdown script.');
  const limit = ['pdf', 'docx'].includes(extension) ? DOCUMENT_FILE_MAX_BYTES : TEXT_FILE_MAX_BYTES;
  if (!Number.isFinite(file.size) || file.size <= 0) throw new Error('That script file is empty.');
  if (file.size > limit) throw new Error(`That ${extension.toUpperCase()} file is too large. Use a file under ${Math.round(limit / 1024 / 1024)} MB or split it into smaller rehearsals.`);
  return extension;
}

export async function textFromPdfDocument(document) {
  const pages = [];
  for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();
    const lines = [];
    let current = '';
    let previousY = null;
    for (const item of content.items || []) {
      const value = String(item.str || '').trim();
      if (!value) continue;
      const y = Number(item.transform?.[5]);
      const changedLine = previousY != null && Number.isFinite(y) && Math.abs(y - previousY) > 2;
      if (changedLine && current.trim()) { lines.push(current.trim()); current = ''; }
      current += `${current && !/\s$/.test(current) ? ' ' : ''}${value}`;
      previousY = Number.isFinite(y) ? y : previousY;
      if (item.hasEOL && current.trim()) { lines.push(current.trim()); current = ''; previousY = null; }
    }
    if (current.trim()) lines.push(current.trim());
    pages.push(lines.join('\n'));
  }
  return cleanExtractedText(pages.join('\n\n'));
}

async function extractPdf(arrayBuffer, adapters = {}) {
  const pdfjs = adapters.pdfjs || await import('pdfjs-dist/legacy/build/pdf.mjs');
  if (!adapters.pdfjs && typeof window !== 'undefined') {
    const { default: workerSrc } = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
  }
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(arrayBuffer) });
  const document = await loadingTask.promise;
  try { return await textFromPdfDocument(document); }
  finally { await document.destroy?.(); }
}

async function extractDocx(arrayBuffer, adapters = {}) {
  const mammoth = adapters.mammoth || await import('mammoth/mammoth.browser.js');
  const result = await mammoth.extractRawText({ arrayBuffer });
  return cleanExtractedText(result.value);
}

export async function extractScriptFile(file, adapters = {}) {
  const extension = validateFile(file);
  let text;
  try {
    if (extension === 'txt' || extension === 'md') text = await file.text();
    else {
      const bytes = await file.arrayBuffer();
      text = extension === 'pdf' ? await extractPdf(bytes, adapters) : await extractDocx(bytes, adapters);
    }
  } catch (error) {
    if (error?.message?.startsWith('That ') || error?.message?.startsWith('Choose ')) throw error;
    throw new Error(`QLine The Game could not read that ${extension.toUpperCase()} file locally. Try saving it again, or export it as TXT.`);
  }
  const cleaned = cleanExtractedText(text);
  if (!cleaned) throw new Error('No readable script text was found in that file. A scanned-image PDF may need OCR before import.');
  if (cleaned.length > MAX_IMPORTED_SCRIPT_CHARACTERS) throw new Error('That script is too long for one rehearsal. Shorten it to 200,000 characters or split it into scenes.');
  return { text: cleaned, extension, title: String(file.name || 'Imported rehearsal').replace(/\.[^.]+$/, '').slice(0, 100) };
}

export const SCRIPT_FILE_ACCEPT = '.pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown';
